// components/ReportTable.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputAdornment, Radio, RadioGroup, Stack, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@mui/material/styles";
import {
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { StyledDataGrid } from '../../../../pages/CustomDataGrid';
import RecordingCell from '../../../../components/common/RecordingCell';
import {
  usePersistedColumnVisibility,
  usePersistedColumnWidths,
  usePersistedColumnOrder,
  usePersistedDensity,
  buildColumnVisibilityKey,
} from '../../../../components/common/usePersistedColumnVisibility';
import { CallStatusTooltip } from './CallStatusTooltip';
import { CustomToolbar } from './CustomToolbar';
import { formatDateTimeJSX } from '../utils/dateFormatter';
import { Table } from 'react-bootstrap';


// Module-level, stable component reference so MUI DataGrid does NOT remount
// the toolbar subtree on every parent render. An inline `() => (...)` here
// would re-mount the search TextField on every keystroke, killing focus
// after the first character. We pass the dynamic bits via `componentsProps`.
const ReportGridToolbar = ({
  searchDestination,
  onSearchDestinationChange,
  alignMode,
  onAlignModeChange,
}) => (
  <GridToolbarContainer sx={{ gap: 1, p: 0.5 }}>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    {/* Flex spacer — pushes search to the centre of the toolbar. */}
    <Box sx={{ flexGrow: 1 }} />
    <TextField
      size="small"
      variant="outlined"
      placeholder="Search Caller ID / DID / Forwarded / Status"
      value={searchDestination || ""}
      onChange={(e) =>
        onSearchDestinationChange && onSearchDestinationChange(e.target.value)
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: searchDestination ? (
          <InputAdornment position="end">
            <IconButton
              edge="end"
              size="small"
              onClick={() =>
                onSearchDestinationChange &&
                onSearchDestinationChange("")
              }
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        width: 280,
        "& .MuiInputBase-root": {
          height: 32,
          fontSize: 13,
          color: "#666",
        },
      }}
    />
    {/* Matching right-side spacer so the search field sits dead-centre
        between the left toolbar buttons and the right-side align radios. */}
    <Box sx={{ flexGrow: 1 }} />
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <FormControl component="fieldset">
        <RadioGroup
          row
          value={alignMode}
          onChange={(e) => onAlignModeChange && onAlignModeChange(e.target.value)}
        >
          <FormControlLabel
            value="left"
            control={<Radio size="small" />}
            label="Align Left"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          />
          <FormControlLabel
            value="right"
            control={<Radio size="small" />}
            label="Align Right"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          />
        </RadioGroup>
      </FormControl>
    </Box>
  </GridToolbarContainer>
);


export const ReportTable = ({
  rows,
  columns,
  loading,
  onRowClick,
  onSelectionChange,
  selectedRows,
  isCallBlockAllowed,
  audioControls,
  searchDestination,
  onSearchDestinationChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiRef = useGridApiRef();
  const prefKey = buildColumnVisibilityKey("user");
  const [columnVisibilityModel, setColumnVisibilityModel] =
    usePersistedColumnVisibility(prefKey);
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);
  const [savedWidths, handleColumnWidthChange] = usePersistedColumnWidths(prefKey);
  const { applyOrder, handleOrderChange } = usePersistedColumnOrder(prefKey, apiRef);
  const { density: savedDensity, handleStateChange: handleDensityStateChange } =
    usePersistedDensity(prefKey, "compact");

  // Align-left / align-right radio buttons that horizontally scroll the grid
  // to start or end (mirrors AdminReport toolbar UX).
  const [alignMode, setAlignMode] = useState("left");
  const tableScrollRef = useRef(null);
  useEffect(() => {
    if (!tableScrollRef.current) return;
    const scroller = tableScrollRef.current.querySelector(".MuiDataGrid-virtualScroller");
    if (!scroller) return;
    // Delay past the canvas-sizing updateColumns pass (100ms+30) and any
    // virtualization layout, then scroll to the far end using the MAX legal
    // scrollLeft (scrollWidth - clientWidth). Setting a huge number also
    // works because browsers clamp, but using the exact value is cleaner.
    const scrollToEdge = () => {
      if (alignMode === "right") {
        const maxLeft = scroller.scrollWidth - scroller.clientWidth;
        scroller.scrollLeft = Math.max(0, maxLeft);
      } else {
        scroller.scrollLeft = 0;
      }
    };
    const t1 = setTimeout(scrollToEdge, 0);
    const t2 = setTimeout(scrollToEdge, 250);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [alignMode, rows]);

  // Uniform header renderer — same font/color across columns, sized via
  // DataGrid sx below so all headers are truly consistent.
  const headerText = (label) => (
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}
    >
      {label}
    </Typography>
  );

  const allColumns = [
    {
      field: "caller_id_number",
      headerName: "Caller ID",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 100,
      renderHeader: () => headerText("Caller ID"),
      renderCell: (params) => (
        <span
          onClick={() => onRowClick(params.row)}
          style={{ fontWeight: 600, color: "#2563eb", cursor: "pointer" }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "did_tfn",
      headerName: "DID Number",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 100,
      renderHeader: () => headerText("DID Number"),
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: "forwarded_number",
      headerName: "Forwarded",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 100,
      renderHeader: () => headerText("Forwarded"),
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: "hangup_reason",
      headerName: "Status",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 150,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" spacing={0.25}>
          {headerText("Status")}
          <Tooltip title="What do these statuses mean?" arrow>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                // Prevent MUI DataGrid from interpreting the click as a
                // column sort / drag — it swallows clicks inside headers.
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setStatusInfoOpen(true);
              }}
              sx={{ p: 0.25, color: "#fff" }}
              aria-label="Status codes info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      renderCell: (params) => {
        const value = params.value || "-";
        return (
          <Tooltip title={value} arrow placement="bottom">
            <span style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
              <CallStatusTooltip statusKey={value} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "call_result",
      headerName: "Call Status",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 90,
      renderHeader: () => headerText("Call Status"),
      renderCell: (params) => (
        <span
          style={{
            color:
              params.row.call_result === "ANSWERED"
                ? "green"
                : params.row.call_result === "MISSED"
                  ? "orange"
                  : "red",
          }}
        >
          {params.row.call_result}
        </span>
      ),
    },
    {
      field: "buyer_name",
      headerName: isMobile ? "Buy. Name" : "Buyer Name",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 130,
      renderHeader: () => headerText(isMobile ? "Buy. Name" : "Buyer Name"),
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 130,
      renderHeader: () => headerText("Campaign Name"),
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 75,
      renderHeader: () => headerText("Duration"),
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: "recording_path",
      headerName: "Recording",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 95,
      renderHeader: () => headerText("Recording"),
      renderCell: (params) => (
        <RecordingCell src={params.row.recording_path} audioControls={audioControls} />
      ),
    },
    {
      field: "start_at",
      headerName: "Call Start",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 130,
      renderHeader: () => headerText("Call Start"),
      renderCell: (params) => formatDateTimeJSX(params.value),
    },
    {
      field: "end_at",
      headerName: "Call End",
      headerClassName: "custom-header",
      headerAlign: "center",
      minWidth: 130,
      flex: 1,
      renderHeader: () => headerText("Call End"),
      renderCell: (params) => formatDateTimeJSX(params.value),
    },
  ];

  const mobileColumns = allColumns.filter(col =>
    ["caller_id_number", "did_tfn", "forwarded_number", "call_result", "buyer_name", "campaign_name", "duration", "recording_path"].includes(col.field)
  );

  const displayColumns = applyOrder(isMobile ? mobileColumns : allColumns);

  // Canvas-based column sizing: measure header + every cell value at the
  // ACTUAL rendered font (14px Roboto), take the max, add cell padding + a
  // visual buffer. MUI's built-in autosizeColumns under-measures because the
  // global `.MuiDataGrid-cellContent { font-size: 12px }` rule in style.css
  // applies to its measurement DOM, while live cells render at 14px (our
  // !important sx). Fixed-px buffers can't compensate for the font mismatch
  // reliably, so we bypass autosize entirely.
  useEffect(() => {
    if (!apiRef.current || !rows?.length) return;
    const t = setTimeout(() => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const headerFont = '700 14px "Roboto","Helvetica","Arial",sans-serif';
        const cellFont = '400 14px "Roboto","Helvetica","Arial",sans-serif';

        const minimums = {};

        // Widget / fixed-format columns — don't canvas-measure the raw value
        // because its width differs from the rendered output.
        //   - recording_path: raw is ~80-char URL, rendered is compact widget
        //   - start_at/end_at/answer_at: raw is ISO string, rendered is
        //     "DD/MM/YYYY HH:MM:SS" via formatDateTimeJSX. Fixed format,
        //     known width, no need for per-row measurement.
        const fixedWidths = {
          recording_path: 140,
          start_at: 145,
          end_at: 145,
          answer_at: 145,
        };

        // IMPORTANT: spread the full column definition — updateColumns
        // REPLACES the entire column entry for each field listed. Passing
        // just {field, width} strips renderCell/renderHeader/headerClassName
        // and wipes all formatting (hyperlinks, bold, colors, etc.).
        const updates = displayColumns.map((col) => {
          // MUI's built-in selection checkbox column — compact fixed width,
          // non-resizable, skips canvas measurement entirely.
          if (col.field === "__check__") {
            return {
              ...col,
              width: 52,
              minWidth: 52,
              maxWidth: 52,
              resizable: false,
            };
          }
          // User's manually-resized width wins over canvas / fixed widths.
          const savedW = savedWidths[col.field];
          if (savedW) {
            return {
              ...col,
              width: savedW,
              maxWidth: Math.max(savedW, 400),
            };
          }
          if (fixedWidths[col.field]) {
            return {
              ...col,
              width: fixedWidths[col.field],
              maxWidth: 400,
            };
          }

          ctx.font = headerFont;
          let maxW = ctx.measureText(String(col.headerName || "")).width;

          ctx.font = cellFont;
          for (const row of rows) {
            const raw = row?.[col.field];
            if (raw == null) continue;
            const w = ctx.measureText(String(raw)).width;
            if (w > maxW) maxW = w;
          }

          const baseMin = minimums[col.field] || 60;
          const computedWidth = Math.max(baseMin, Math.ceil(maxW) + 20);
          return {
            ...col,
            width: computedWidth,
            // Override any column-level maxWidth cap so canvas-measured
            // width isn't silently clamped smaller than content needs.
            maxWidth: Math.max(computedWidth, 400),
          };
        });

        apiRef.current.updateColumns(updates);
      } catch (_) { /* grid not ready */ }
    }, 80);
    return () => clearTimeout(t);
    // displayColumns is a new array every render — including it would
    // loop this effect. rows.length is a stable number that reflects
    // actual data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows?.length, isMobile, savedWidths]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "90%", marginTop: "9rem" }}>
        <CircularProgress />
      </Box>
    );
  }



  // const handleSelectAll = (e) => {
  //   if (e.target.checked) {
  //     const allIds = rows.map((r) => r.id);
  //     setLocalSelected(allIds);
  //     onSelectionChange && onSelectionChange(allIds);
  //   } else {
  //     setLocalSelected([]);
  //     onSelectionChange && onSelectionChange([]);
  //   }
  // };

  // const handleRowSelect = (id) => {
  //   let updated;
  //   if (localSelected.includes(id)) {
  //     updated = localSelected.filter((x) => x !== id);
  //   } else {
  //     updated = [...localSelected, id];
  //   }
  //   setLocalSelected(updated);
  //   onSelectionChange && onSelectionChange(updated);
  // };


  return (
    <>

      {/* <div style={{ width: "100%", overflow: "hidden" }}>
  <StyledDataGrid
    className="custom_header_redirect"
    rows={rows}
    columns={displayColumns}
    density="compact"
    checkboxSelection={isCallBlockAllowed}
    disableRowSelectionOnClick
    rowSelectionModel={selectedRows}
    onRowSelectionModelChange={onSelectionChange}
    autoHeight={rows.length < 11}
  />
</div> */}


      <Box
        sx={{
          width: "100%",
          height: { xs: "100%", md: "73vh" },
        }}
      >

        <div ref={tableScrollRef} style={{ height: "100%", width: "100%" }}>
        <StyledDataGrid
          apiRef={apiRef}
          className="custom_header_redirect"
          rows={rows}
          columns={displayColumns}
          initialState={{ density: savedDensity }}
          onStateChange={handleDensityStateChange}
          rowHeight={36}
          columnHeaderHeight={36}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={onSelectionChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          onColumnWidthChange={handleColumnWidthChange}
          onColumnOrderChange={handleOrderChange}
          autoHeight={rows.length < 8}
          components={{ Toolbar: ReportGridToolbar }}
          componentsProps={{
            toolbar: {
              searchDestination,
              onSearchDestinationChange,
              alignMode,
              onAlignModeChange: setAlignMode,
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              position: "sticky",
              top: 0,
              zIndex: 99,
              backgroundColor: "#fff",
            },
            // Kill only the ellipsis — keep per-cell font sizes/colors/bold
            // and flex/block layouts intact. Canvas-measured widths +40px
            // buffer guarantee text fits without needing `...`.
            "& .MuiDataGrid-cell, & .MuiDataGrid-cellContent": {
              textOverflow: "clip !important",
            },
            "& .MuiDataGrid-cell": {
              paddingTop: "2px",
              paddingBottom: "2px",
            },
            "& .MuiDataGrid-filler, & .MuiDataGrid-columnHeader--filler, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-cell--filler, & .MuiDataGrid-row--filler": {
              display: "none !important",
            },
          }}
        />
        </div>

      </Box>

      {/* Status codes info dialog — triggered by the (i) button in the
          Status column header. Follows the SIP Tracer "How it works" pattern. */}
      <Dialog
        open={statusInfoOpen}
        onClose={() => setStatusInfoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Call Status codes
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setStatusInfoOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The <b>Status</b> column shows the final outcome code written by
            the call-routing engine. Every code rolls up into one of three
            <b> Call Status </b> categories: <b>ANSWERED</b>, <b>MISSED</b>, or
            <b> FAILED</b>.
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#047857", mb: 0.5 }}>
            ANSWERED — the call was picked up
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
            <li><code>BUYER_ANSWERED</code> — a buyer in the DID's campaign picked up the forwarded call.</li>
          </Box>

          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d97706", mt: 2, mb: 0.5 }}>
            MISSED — call reached the routing layer but nobody answered
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
            <li><code>BUYER_BUSY</code> — buyer's line was busy (SIP 486/600).</li>
            <li><code>BUYER_NOANSWER</code> — buyer didn't answer within the campaign's ring timeout.</li>
            <li><code>BUYER_NOT_FOUND</code> — no active buyer was available in the campaign (all at daily limit or disabled).</li>
            <li><code>CALLER_CANCEL</code> — the caller hung up before any buyer answered.</li>
            <li><code>COMPAIGN_USER_MISMATCH</code> — the DID's campaign doesn't belong to the DID's owner (configuration error).</li>
            <li><code>DID_COMPAIGN_MISSING</code> — the DID has no campaign assigned.</li>
            <li><code>NON_OFFICE_HOUR</code> — call came outside the campaign's working-hours window.</li>
            <li><code>NOT_SUFFICIENT_FUNDS</code> — user's account balance is below the minimum; call was rejected before dialing.</li>
          </Box>

          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#b91c1c", mt: 2, mb: 0.5 }}>
            FAILED — call was rejected or blocked outright
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
            <li><code>CALLERID_BLOCKED_BY_USER</code> — caller's number is on the DID owner's block list.</li>
            <li><code>CONGESTION</code> — trunk or network congestion.</li>
            <li><code>DID_NOT_ACTIVE</code> — the DID is deactivated.</li>
            <li><code>DID_SUSPENDED</code> — the DID has been suspended by admin.</li>
            <li><code>DID_USER_NOT_ACTIVE</code> — the user who owns this DID is inactive.</li>
            <li><code>IVR_AUTHENDICATION_FAILED</code> — caller entered the wrong PIN (or no PIN) at the IVR auth prompt.</li>
            <li><code>TRIED_NO_SUCCESS</code> — all buyers in the campaign were tried, none answered. Distinct from <code>BUYER_NOANSWER</code> (single buyer) — this is the whole campaign exhausted.</li>
            <li><code>USER_NOT_FOUND</code> — the DID lookup didn't return any owner.</li>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Occasional edge-case codes (e.g. <code>NO_CARRIERS</code>,
            <code> BILLING_INFO_NOT_FOUND</code>,
            <code> CALLERID_VOIP_REJCTION</code>,
            <code> SHAKEN_VALIDATION_FAILED</code>) may also appear. Contact support if you see those frequently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusInfoOpen(false)} size="small" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* <StyledDataGrid
       rows={rows}
       density="compact"
       columns={displayColumns}
       components={{ Toolbar: CustomToolbar }}
       autoHeight
       disableColumnResize={false}
       checkboxSelection={isCallBlockAllowed}
       disableRowSelectionOnClick
       rowSelectionModel={selectedRows}
       onRowSelectionModelChange={onSelectionChange}
       sx={{
         "& .MuiDataGrid-cell": {
           fontSize: { xs: "12px", sm: "14px", md: "13px" },
           wordBreak: "break-word",
           whiteSpace: "break-spaces",
         },
       }}
     /> */}



      {/* // bootstrap report table */}

      {/* <div style={{ overflow: "auto", maxHeight: "70vh", marginTop: "50px" }}>
    <Table hover size="sm" responsive bordered className="call-active-table report_table border-1">
    
    <thead
      style={{
        position: "sticky",
        top: 0,
        background: "#1f4d3a",
        color: "#fff",
        zIndex: 2,
      }}
    >
      <tr  className="active-table-head ">
        {isCallBlockAllowed && (
          <th style={{ width: 40, background:"#0a140e ", color: "#fff", alignItems: "center" }}>
            <input
            style={{width: "15px", height: "15px",}}
              type="checkbox"
              onChange={handleSelectAll}
              checked={localSelected.length === rows.length && rows.length > 0}
            />
          </th>
        )}

        {allColumns.map((col) => (
          <th key={col.field} style={{background:"#0a140e ", color: "#fff", alignItems: "center"}}>{col.headerName} </th>
        ))}
      </tr>
    </thead>

    
    <tbody>
      {rows.map((row) => (
        <tr key={row.id}>
          {isCallBlockAllowed && (
            <td>
              <input
                type="checkbox"
                style={{width: "15px", height: "15px",}}
                checked={localSelected.includes(row.id)}
                onChange={() => handleRowSelect(row.id)}
              />
            </td>
          )}

          {allColumns.map((col) => (
            <td key={col.field} >
              {col.renderCell
                ? col.renderCell({
                    row: row,
                    value: row[col.field],
                  })
                : row[col.field]}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </Table>

</div> */}





    </>

  );
};